import { clamp } from '../core/math';
import { EState } from './engine';
import { ignitionThreshold } from './fire';
import { Car, CellState, GRID_H, GRID_W, PState, Person, Road, idx } from './types';
import type { World } from './world';

const MIN_GAP = 3.2;
const MAX_V = 5.0;
const ACCEL = 3.2;
const DECEL = 7.0;
const CAR_SEATS = 4;
/** Seconds a car waits at the pickup for the rest of the family. */
const BOARD_WAIT = 2.2;
/** Seconds stuck in a queue before people give up and get out. */
const ABANDON_AFTER = 18;
/** Flame intensity in or beside a road cell that stops traffic. */
const BLOCK_INTENSITY = 0.3;
/** Cells either side of a fire engine where residents' cars have to squeeze past. */
const ENGINE_SQUEEZE = 3;

const roadCell = (road: Road, s: number) => road.cells[clamp(Math.round(s), 0, road.cells.length - 1)];

/**
 * Work out where each road is impassable. Roads themselves do not burn. What
 * closes a road is flame in the cells beside it. Once the fire has moved on you
 * can drive slowly over the burnt ground it leaves behind.
 */
export function updateRoadStatus(w: World): void {
  const { grid } = w;
  for (const road of w.roads) {
    const n = road.cells.length;
    if (road.blocked.length !== n) road.blocked = new Float32Array(n);
    if (road.blockedBehind.length !== n) road.blockedBehind = new Float32Array(n);
    if (road.riskAhead.length !== n) road.riskAhead = new Float32Array(n);
    if (road.scorched.length !== n) road.scorched = new Uint8Array(n);

    let firstCut: number | null = null;
    // Pass 1: is this stretch blocked by flame, close to catching, or just burnt?
    const hot = new Uint8Array(n);
    const risky = new Uint8Array(n);
    for (let s = 0; s < n; s++) {
      const c = road.cells[s];
      let maxIn = 0;
      let burnt = 0;
      let heat = 0;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const x = c.x + dx;
          const y = c.y + dy;
          if (x < 0 || y < 0 || x >= GRID_W || y >= GRID_H) continue;
          const i = idx(x, y);
          const near = Math.abs(dx) <= 1 && Math.abs(dy) <= 1;
          if (grid.state[i] === CellState.Burning) {
            if (near) maxIn = Math.max(maxIn, grid.inten[i]);
            heat = Math.max(heat, 1);
          } else if (grid.state[i] === CellState.Burnt) {
            if (near) burnt++;
          } else {
            heat = Math.max(heat, grid.heat[i] / Math.max(0.05, ignitionThreshold(grid, i)));
          }
        }
      }
      hot[s] = maxIn > BLOCK_INTENSITY ? 1 : 0;
      // Flag "about to catch" as well as "already alight". A crew needs warning
      // before the road closes, not a report after it has closed.
      risky[s] = hot[s] || heat > 0.45 ? 1 : 0;
      road.scorched[s] = burnt >= 3 ? 1 : 0;
      if (hot[s] && firstCut === null) firstCut = s;
    }
    // Pass 2: nearest block at or ahead (cars and withdrawing crews drive
    // towards +s) and at or behind (fire engines drive uphill towards -s).
    let ahead = Infinity;
    let risk = Infinity;
    for (let s = n - 1; s >= 0; s--) {
      if (hot[s]) ahead = s;
      if (risky[s]) risk = s;
      road.blocked[s] = ahead;
      road.riskAhead[s] = risk;
    }
    let behind = Infinity;
    for (let s = 0; s < n; s++) {
      if (hot[s]) behind = s;
      road.blockedBehind[s] = behind;
    }
    road.cut = firstCut;
  }
}

function speedLimit(w: World, road: Road, s: number): number {
  const i = clamp(Math.round(s), 0, road.cells.length - 1);
  let base = Math.min(MAX_V, road.cells[i].limit);
  if (road.scorched[i]) base *= 0.55;

  // Your own fire engines are an obstacle. These are single-track mountain
  // roads: an engine driving up slows the traffic coming down, and an engine
  // stopped and working at a bend nearly closes the road. That is the real cost
  // of sending engines up the hill.
  for (const e of w.engines) {
    if (e.roadId !== road.id) continue;
    if (e.state === EState.Responding || e.state === EState.Lost) continue;
    const gap = Math.abs(e.s - s);
    if (gap > ENGINE_SQUEEZE) continue;
    base *= e.state === EState.Working || e.state === EState.Resupply ? 0.28 : 0.5;
    break;
  }
  return Math.max(0.25, base);
}

export function stepTraffic(w: World, dt: number): void {
  const byRoad: Car[][] = w.roads.map(() => []);
  for (const car of w.cars) byRoad[car.roadId].push(car);

  for (const road of w.roads) {
    const cars = byRoad[road.id];
    cars.sort((a, b) => b.s - a.s); // nearest the exit first
    const n = road.cells.length;
    let moving = 0;
    // A car still picking up its family is not stuck in traffic, so it must not
    // count towards the jam figure. One car loading up used to show as JAMMED.
    let underway = 0;

    for (let k = 0; k < cars.length; k++) {
      const car = cars[k];

      if (car.boarding > 0) {
        car.boarding -= dt;
        car.v = 0;
        continue;
      }
      underway++;

      // Whichever comes first: the car in front, or the fire across the road ahead.
      let limitS = Infinity;
      const leader = cars[k - 1];
      if (leader) limitS = leader.s - MIN_GAP;
      const block = road.blocked[clamp(Math.round(car.s), 0, n - 1)];
      if (Number.isFinite(block)) limitS = Math.min(limitS, block - 1.5);

      const room = limitS - car.s;
      const want = Number.isFinite(room) ? clamp(room * 2.2, 0, speedLimit(w, road, car.s)) : speedLimit(w, road, car.s);
      const rate = want > car.v ? ACCEL : DECEL;
      car.v += clamp(want - car.v, -rate * dt, rate * dt);
      if (car.v < 0) car.v = 0;
      car.s += car.v * dt;
      if (car.v > 0.4) moving++;

      car.stalled = car.v < 0.15 ? car.stalled + dt : 0;

      // Keep the occupants' positions in step with the car so heat can reach them.
      const cell = roadCell(road, car.s);
      for (const id of car.occupants) {
        const p = w.people[id];
        p.x = cell.x + 0.5;
        p.y = cell.y + 0.5;
      }

      // Off the map by road: safe.
      if (car.s >= n - 1) {
        for (const id of car.occupants) {
          const p = w.people[id];
          p.state = PState.Safe;
          p.carId = -1;
        }
        car.occupants = [];
        road.through++;
        continue;
      }

      // Trapped in the queue with fire coming: get out and walk.
      const nearFire = Number.isFinite(block) && block - car.s < 6;
      if (car.stalled > ABANDON_AFTER || nearFire) {
        for (const id of car.occupants) {
          const p = w.people[id];
          p.carId = -1;
          p.roadId = -1;
          p.state = PState.Walking;
          p.think = 0;
        }
        w.say(
          nearFire
            ? `Fire across the ${road.name}. Drivers are abandoning their cars.`
            : `Traffic at a standstill on the ${road.name}. People are getting out and walking.`,
          'warn',
        );
        car.occupants = [];
      }
    }

    road.jam = underway === 0 ? 0 : 1 - moving / underway;
  }

  w.cars = w.cars.filter((c) => c.occupants.length > 0 && c.s < w.roads[c.roadId].cells.length - 1);
}

/**
 * A person has reached their road. Put them in a car if one is already loading
 * up, or if there is room to pull a new car onto the road. Otherwise leave them
 * standing at the pickup point.
 */
export function tryBoard(w: World, p: Person, road: Road, accessS: number): boolean {
  for (const car of w.cars) {
    if (car.roadId !== road.id) continue;
    if (car.occupants.length >= CAR_SEATS) continue;
    if (Math.abs(car.s - accessS) > 1.5) continue;
    if (car.boarding <= 0) continue;
    car.occupants.push(p.id);
    car.boarding = Math.max(car.boarding, BOARD_WAIT * 0.6);
    p.state = PState.InCar;
    p.carId = car.id;
    return true;
  }

  // Is there physical room to pull a car onto the road here?
  for (const car of w.cars) {
    if (car.roadId === road.id && Math.abs(car.s - accessS) < MIN_GAP * 0.8) return false;
  }
  const car: Car = {
    id: w.nextCarId++,
    roadId: road.id,
    s: accessS,
    v: 0,
    occupants: [p.id],
    stalled: 0,
    boarding: BOARD_WAIT,
  };
  w.cars.push(car);
  p.state = PState.InCar;
  p.carId = car.id;
  return true;
}

/** Cars queueing on a road, for the HUD. */
export function carsOn(w: World, roadId: number): number {
  let n = 0;
  for (const c of w.cars) if (c.roadId === roadId) n++;
  return n;
}
